'use es6';

import { Fragment, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import styled from 'styled-components';
import I18n from 'I18n';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import Big from 'UIComponents/elements/Big';
import UIBreakString from 'UIComponents/text/UIBreakString';
import UIClickable from 'UIComponents/button/UIClickable';
import UIGrid from 'UIComponents/grid/UIGrid';
import UIGridItem from 'UIComponents/grid/UIGridItem';
import UIImage from 'UIComponents/image/UIImage';
import UIButtonWrapper from 'UIComponents/layout/UIButtonWrapper';
import UILightbox from 'UIComponents/lightbox/UILightbox';
import UISection from 'UIComponents/section/UISection';
import UIMediaObject from 'UIComponents/layout/UIMediaObject';
import UILink from 'UIComponents/link/UILink';
import LoadingWrapper from 'ecosystem-marketplace-core/components/LoadingWrapper';
import IntegrationFeature from 'ecosystem-marketplace-core/records/IntegrationFeature';
import { useFocusCloseButton } from 'marketplace-ui-common/hooks/useFocusCloseButton';
import { getTierI18nKey } from 'marketplace-ui-common/utils/i18n';
import { makeGetRequiredPlansForFeatures } from '../../../selectors/details';
import { getIsCustomer } from '../../../selectors/auth';
import { getFeatureCompatibilityRequestState } from '../../../selectors/requestStates';
import { getPricingUrl } from '../../../utils/urls';
import CompatibilityIcon from '../../common/CompatibilityIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const IMAGE_WIDTH = 280;
const FeatureParagraph = styled.p.withConfig({
  displayName: "IntegrationFeatures__FeatureParagraph",
  componentId: "ysvomx-0"
})(["white-space:pre-line;"]);
const IntegrationFeatures = ({
  onTrackInteraction,
  isOpsHubListing,
  integrationFeatures
}) => {
  const [focusedIndex, setFocusedIndex] = useState(null);
  const isFocused = focusedIndex != null;
  useFocusCloseButton(isFocused);
  const renderedLightbox = useMemo(() => {
    const integrationFeature = integrationFeatures.get(focusedIndex);
    if (!integrationFeature) {
      return null;
    }
    return /*#__PURE__*/_jsx(UILightbox, {
      imageAlt: integrationFeature.get('text'),
      imageSrc: integrationFeature.get('imageUrl'),
      onClose: () => setFocusedIndex(null),
      children: /*#__PURE__*/_jsx(UIButtonWrapper, {
        children: integrationFeature.get('text')
      })
    });
  }, [integrationFeatures, focusedIndex]);
  const featureCompatibilityRequestStatus = useSelector(getFeatureCompatibilityRequestState);
  const isCustomer = useSelector(getIsCustomer);
  const getRequiredPlans = useMemo(makeGetRequiredPlansForFeatures, []);
  const requiredPlans = useSelector(state => getRequiredPlans(state, integrationFeatures));
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [integrationFeatures.map((integrationFeature, index) => {
      const {
        headline,
        text,
        imageUrl,
        hubSpotFeatures
      } = integrationFeature;
      const requiredPlan = requiredPlans && requiredPlans.get(index);
      const requiredPlanHub = requiredPlan && requiredPlan.split('_')[0];
      const showFeatureCompatibility = hubSpotFeatures && hubSpotFeatures.size && isCustomer && isOpsHubListing;
      return /*#__PURE__*/_jsx(UISection, {
        className: "m-bottom-8",
        children: /*#__PURE__*/_jsxs(UIGrid, {
          children: [/*#__PURE__*/_jsxs(UIGridItem, {
            size: {
              sm: 12,
              md: 7
            },
            children: [/*#__PURE__*/_jsx(Big, {
              weight: "bold",
              children: /*#__PURE__*/_jsx(UIBreakString, {
                children: headline
              })
            }), /*#__PURE__*/_jsx(FeatureParagraph, {
              children: /*#__PURE__*/_jsx(UIBreakString, {
                children: text
              })
            }), showFeatureCompatibility ? /*#__PURE__*/_jsx(LoadingWrapper, {
              requestStatus: featureCompatibilityRequestStatus,
              children: /*#__PURE__*/_jsx(UIMediaObject, {
                align: "start",
                itemLeft: /*#__PURE__*/_jsx(CompatibilityIcon, {
                  compatible: !requiredPlan
                }),
                spacing: "extra-small",
                children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
                  message: `appMarketplace.detail.overview.integrationFeatures.${requiredPlan ? 'notCompatible_jsx' : 'compatible_jsx'}`,
                  options: {
                    planName: requiredPlan ? I18n.text(getTierI18nKey(requiredPlan)) : '',
                    linkProps: {
                      external: true,
                      href: getPricingUrl(isCustomer, requiredPlanHub),
                      onClick: () => onTrackInteraction('click feature required plan pricing link', 'overview', requiredPlanHub)
                    }
                  },
                  elements: {
                    Bold: 'b',
                    Link: UILink
                  }
                })
              })
            }) : null]
          }), /*#__PURE__*/_jsx(UIGridItem, {
            size: {
              sm: 12,
              md: 5
            },
            children: /*#__PURE__*/_jsx(UIClickable, {
              onClick: () => {
                setFocusedIndex(index);
                onTrackInteraction('click integration feature screenshot', 'overview');
              },
              children: /*#__PURE__*/_jsx(UIImage, {
                alt: headline,
                responsive: true,
                src: imageUrl,
                width: IMAGE_WIDTH
              })
            })
          })]
        })
      }, `integration-feature-${index}`);
    }), renderedLightbox]
  });
};
IntegrationFeatures.propTypes = {
  onTrackInteraction: PropTypes.func.isRequired,
  isOpsHubListing: PropTypes.bool.isRequired,
  integrationFeatures: ImmutablePropTypes.listOf(PropTypes.instanceOf(IntegrationFeature)).isRequired
};
export default IntegrationFeatures;